import { useState, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/components/ui';
import { useFieldTemplates } from '@/hooks/useFieldTemplates';
import type {
  CreateFormFieldDto,
  CreateFieldTemplateDto,
  FormFieldType,
  FieldTemplate,
  FormFieldWithValues,
} from '@/types/form';
import { FORM_FIELD_TYPE_LABELS, FIELD_TEMPLATE_CATEGORY_LABELS } from '@/types/form';
import { Check, Loader2, Search } from 'lucide-react';

interface AddFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (
    field: Omit<CreateFormFieldDto, 'form_id' | 'order_index'>,
    orderIndex?: number
  ) => Promise<void>;
  currentFieldsCount: number;
  existingFields: FormFieldWithValues[];
}

/**
 * Modal pour ajouter un nouveau champ à un formulaire
 * Offre le choix entre créer un nouveau champ ou sélectionner un template
 */
export function AddFieldModal({
  isOpen,
  onClose,
  onAdd,
  currentFieldsCount,
  existingFields,
}: AddFieldModalProps) {
  const [activeTab, setActiveTab] = useState<'new' | 'template'>('template');
  const [selectedTemplates, setSelectedTemplates] = useState<FieldTemplate[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // État du formulaire personnalisé
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState<FormFieldType>('text');
  const [placeholder, setPlaceholder] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [options, setOptions] = useState('');

  const { templatesByCategory, isLoading, createTemplate } = useFieldTemplates();

  // IDs des templates déjà utilisés dans le formulaire
  const existingTemplateIds = useMemo(
    () => new Set(existingFields.map((field) => field.field_template_id).filter(Boolean)),
    [existingFields]
  );

  // Filtrer et organiser les templates
  const filteredTemplatesByCategory = useMemo(() => {
    const filtered: Record<string, FieldTemplate[]> = {};

    Object.entries(templatesByCategory).forEach(([category, templates]) => {
      const filteredTemplates = (templates as FieldTemplate[]).filter((template) => {
        const matchesSearch =
          searchQuery === '' ||
          template.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
          template.name.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesSearch;
      });

      if (filteredTemplates.length > 0) {
        filtered[category] = filteredTemplates;
      }
    });

    return filtered;
  }, [templatesByCategory, searchQuery]);

  // Vérifier si un template est déjà utilisé
  const isTemplateUsed = (templateId: string) => existingTemplateIds.has(templateId);

  const handleSubmitNew = async () => {
    if (!label) return;

    setIsSubmitting(true);
    try {
      // 1. Créer d'abord le template en base
      const templateData: CreateFieldTemplateDto = {
        name: label,
        label,
        field_type: fieldType,
        placeholder: placeholder || undefined,
        is_required: isRequired,
        options: options
          ? options
              .split(',')
              .map((opt) => opt.trim())
              .filter(Boolean)
          : undefined,
        category: 'default',
        is_system: false,
      };

      const newTemplate = await createTemplate(templateData);

      if (!newTemplate) {
        toast.error('Erreur lors de la création du template');
        return;
      }

      // 2. Ajouter le champ au formulaire avec le template_id
      await onAdd(
        {
          field_template_id: newTemplate.id,
        },
        currentFieldsCount
      );

      resetForm();
      onClose();
    } catch (error) {
      console.error('Error creating field:', error);
      toast.error('Erreur lors de la création du champ');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectTemplate = async () => {
    if (selectedTemplates.length === 0) return;

    setIsSubmitting(true);
    try {
      // Ajouter tous les champs sélectionnés au formulaire avec des order_index calculés d'avance
      for (let i = 0; i < selectedTemplates.length; i++) {
        const template = selectedTemplates[i];
        const orderIndex = currentFieldsCount + i;
        await onAdd(
          {
            field_template_id: template.id,
          },
          orderIndex
        );
      }

      toast.success(
        selectedTemplates.length === 1
          ? 'Champ ajouté avec succès'
          : `${selectedTemplates.length} champs ajoutés avec succès`
      );

      resetForm();
      onClose();
    } catch (error) {
      console.error('Error adding fields:', error);
      toast.error("Erreur lors de l'ajout des champs");
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleTemplateSelection = (template: FieldTemplate) => {
    // Empêcher la sélection des templates déjà utilisés
    if (isTemplateUsed(template.id)) {
      toast.error('Ce champ est déjà présent dans le formulaire');
      return;
    }

    setSelectedTemplates((prev) => {
      const isSelected = prev.some((t) => t.id === template.id);
      if (isSelected) {
        return prev.filter((t) => t.id !== template.id);
      } else {
        return [...prev, template];
      }
    });
  };

  const resetForm = () => {
    setLabel('');
    setFieldType('text');
    setPlaceholder('');
    setIsRequired(false);
    setOptions('');
    setSelectedTemplates([]);
    setSearchQuery('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Options pour le select du type de champ
  const fieldTypeOptions = Object.keys(FORM_FIELD_TYPE_LABELS) as FormFieldType[];

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Ajouter un champ" size="3xl">
      <Tabs value={activeTab} onValueChange={(v: string) => setActiveTab(v as 'new' | 'template')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="template">Templates</TabsTrigger>
          <TabsTrigger value="new">Nouveau champ</TabsTrigger>
        </TabsList>

        {/* TAB: Sélection d'un template */}
        <TabsContent value="template" className="space-y-4 mt-4">
          {/* Barre de recherche */}
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500"
              aria-hidden="true"
            />
            <Input
              type="search"
              placeholder="Rechercher un template..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 bg-white border-zinc-200 shadow-sm"
            />
          </div>

          {isLoading ? (
            <p className="text-sm text-gray-500 text-center py-8">Chargement des templates...</p>
          ) : Object.keys(filteredTemplatesByCategory).length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              {searchQuery
                ? 'Aucun template ne correspond à votre recherche'
                : 'Aucun template disponible'}
            </p>
          ) : (
            <div className="space-y-4 max-h-[25rem] overflow-y-auto">
              {Object.entries(filteredTemplatesByCategory).map(([category, templates]) => (
                <div key={category}>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">
                    {FIELD_TEMPLATE_CATEGORY_LABELS[category] || category}
                  </h4>
                  <div className="space-y-2">
                    {(templates as FieldTemplate[]).map((template) => {
                      const isSelected = selectedTemplates.some((t) => t.id === template.id);
                      const isUsed = isTemplateUsed(template.id);
                      return (
                        <Card
                          key={template.id}
                          className={`p-3 transition-all ${
                            isUsed
                              ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-300'
                              : isSelected
                                ? 'border-blue-500 bg-blue-50 cursor-pointer hover:shadow-md'
                                : 'border-gray-200 cursor-pointer hover:shadow-md'
                          }`}
                          onClick={() => !isUsed && toggleTemplateSelection(template)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span
                                  className={`font-medium text-sm ${isUsed ? 'text-gray-500' : ''}`}
                                >
                                  {template.label}
                                </span>
                                {isUsed && (
                                  <Badge variant="outline" className="text-xs text-gray-500">
                                    Déjà présent
                                  </Badge>
                                )}
                                {template.is_required && !isUsed && (
                                  <Badge variant="destructive" className="text-xs">
                                    Requis
                                  </Badge>
                                )}
                                <Badge variant="secondary" className="text-xs">
                                  {FORM_FIELD_TYPE_LABELS[template.field_type]}
                                </Badge>
                                {template.target_role === 'pilot' && (
                                  <Badge
                                    variant="outline"
                                    className="text-xs text-amber-700 border-amber-300 bg-amber-50"
                                  >
                                    Pilote
                                  </Badge>
                                )}
                                {template.target_role === 'copilot' && (
                                  <Badge
                                    variant="outline"
                                    className="text-xs text-sky-700 border-sky-300 bg-sky-50"
                                  >
                                    Copilote
                                  </Badge>
                                )}
                              </div>
                              {template.placeholder && (
                                <p className="text-xs text-gray-500">
                                  Placeholder: {template.placeholder}
                                </p>
                              )}
                              {template.contact_field_map && (
                                <p
                                  className={`text-xs ${isUsed ? 'text-gray-400' : 'text-blue-600'}`}
                                >
                                  → Mappé sur: {template.contact_field_map}
                                </p>
                              )}
                            </div>
                            {isSelected && !isUsed && (
                              <Check className="h-5 w-5 text-blue-600 flex-shrink-0" />
                            )}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* TAB: Création d'un nouveau champ */}
        <TabsContent value="new" className="my-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="label">Label *</Label>
            <Input
              id="label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex: Prénom, Email, Téléphone..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="field_type">Type de champ *</Label>
            <Select
              id="field_type"
              value={fieldType}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setFieldType(e.target.value as FormFieldType)
              }
            >
              {fieldTypeOptions.map((type) => (
                <option key={type} value={type}>
                  {FORM_FIELD_TYPE_LABELS[type]}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="placeholder">Placeholder</Label>
            <Input
              id="placeholder"
              value={placeholder}
              onChange={(e) => setPlaceholder(e.target.value)}
              placeholder="Ex: Entrez votre prénom"
            />
          </div>

          <Checkbox checked={isRequired} onChange={setIsRequired} label="Champ requis" />

          {/* Options pour select/radio/checkbox */}
          {['select', 'radio', 'checkbox'].includes(fieldType) && (
            <div className="space-y-2">
              <Label htmlFor="options">Options</Label>
              <Textarea
                id="options"
                value={options}
                onChange={(e) => setOptions(e.target.value)}
                placeholder="Ex: Option 1, Option 2, Option 3 (séparées par des virgules)"
                rows={3}
              />
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="flex items-center justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
          Annuler
        </Button>
        {activeTab === 'template' ? (
          <Button
            onClick={handleSelectTemplate}
            disabled={selectedTemplates.length === 0 || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Ajout en cours...
              </>
            ) : selectedTemplates.length === 0 ? (
              'Sélectionner un template'
            ) : selectedTemplates.length === 1 ? (
              'Ajouter 1 champ'
            ) : (
              `Ajouter ${selectedTemplates.length} champs`
            )}
          </Button>
        ) : (
          <Button onClick={handleSubmitNew} disabled={!label || isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Création en cours...
              </>
            ) : (
              'Créer le champ'
            )}
          </Button>
        )}
      </div>
    </Modal>
  );
}
